import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  MutationFunction,
  UseMutationOptions,
} from '@tanstack/react-query';
import type { TRPCClient, TRPCClientErrorLike } from '@trpc/client';
import type {
  TRPCOptionsProxy,
  TRPCQueryKey,
} from '@trpc/tanstack-react-query';

import type { AppRouter } from '@infinex/main-service-contract/v2/client';
import type { MainServiceTrpcClient as Client } from '@infinex/user';

import type { BaseRecord } from '../stores';

export type QueryOptions<Response, Key extends QueryKey> = Omit<
  UseQueryOptions<Response, Error, Response, Key>,
  'queryKey' | 'queryFn'
>;

export type QueryOptionsV2<Response, Data = Response> = Omit<
  UseQueryOptions<Response, TRPCClientErrorLike<AppRouter>, Data, TRPCQueryKey>,
  'queryKey' | 'queryFn'
>;

export type MutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, Error, TVariables, unknown>,
  'mutationFn'
>;

// Types for the configuration
export type QueryHookFactory = <Response, Key extends QueryKey>(queryConfig: {
  queryKey: Key;
  queryFn: QueryFunction<Response, Key>;
}) => (
  options?: Omit<
    UseQueryOptions<Response, Error, Response, Key>,
    'queryKey' | 'queryFn'
  >
) => UseQueryResult<Response, Error>;

export type MutationHookFactory = <
  TData,
  TVariables,
  TOptimisticData extends BaseRecord[],
>(
  mutationFn: MutationFunction<TData, TVariables>
) => (
  options?: Omit<
    UseMutationOptions<TData, Error, TVariables, unknown>,
    'mutationFn'
  >,
  authElevationOptions?: any,
  optimisticOptions?: {
    recordKey: string;
    optimisticMapFn: (data: TVariables) => TOptimisticData[number];
  }
) => any;

export interface WebSharedApiConfig {
  // API configuration
  api?: {
    client: Client;
    createQueryHook: QueryHookFactory;
    createMutationHook: MutationHookFactory;
  };
  // TRPC client configuration
  apiV2?: TRPCClient<AppRouter>;
  trpcClientV2?: TRPCOptionsProxy<AppRouter>;
  // Worker main URL for public endpoints
  workerMainUrl?: string;
}

let config: WebSharedApiConfig | null = null;

/**
 * Internal configuration function - DO NOT CALL DIRECTLY.
 * Use configureWebShared() instead and pass api, apiV2, or trpcClientV2.
 * @internal
 */
export function configureWebSharedApi(newConfig: WebSharedApiConfig): void {
  config = newConfig;
}

/**
 * Get the configured client instance.
 * Throws if configureWebShared has not been called or API is not configured.
 */
export function getClient(): Client {
  if (!config) {
    throw new Error(
      'Web Shared API not configured. Please call configureWebShared() with API configuration before using API hooks.'
    );
  }
  if (!config.api) {
    throw new Error(
      'API not configured. Please include api configuration in your configureWebShared() call.'
    );
  }
  return config.api.client;
}

/**
 * Get the configured query hook factory.
 * Throws if configureWebShared has not been called or API is not configured.
 */
export function getQueryHookFactory(): QueryHookFactory {
  if (!config) {
    throw new Error(
      'Web Shared API not configured. Please call configureWebShared() with API configuration before using API hooks.'
    );
  }
  if (!config.api) {
    throw new Error(
      'API not configured. Please include api configuration in your configureWebShared() call.'
    );
  }
  return config.api.createQueryHook;
}

/**
 * Get the configured mutation hook factory.
 * Throws if configureWebShared has not been called or API is not configured.
 */
export function getMutationHookFactory(): MutationHookFactory {
  if (!config) {
    throw new Error(
      'Web Shared API not configured. Please call configureWebShared() with API configuration before using API hooks.'
    );
  }
  if (!config.api) {
    throw new Error(
      'API not configured. Please include api configuration in your configureWebShared() call.'
    );
  }
  return config.api.createMutationHook;
}

/**
 * Get the configured TRPC client proxy.
 * Throws if configureWebShared has not been called or trpcClientV2 not configured.
 */
export function getTRPCClient(): TRPCOptionsProxy<AppRouter> {
  if (!config) {
    throw new Error(
      'Web Shared API not configured. Please call configureWebShared() with API configuration before using trpc client.'
    );
  }
  if (!config.trpcClientV2) {
    throw new Error(
      'TRPC client proxy not configured. Please include trpcClientV2 in your configureWebShared() call.'
    );
  }
  return config.trpcClientV2;
}

/**
 * Get the configured TRPC client instance.
 * Throws if configureWebShared has not been called or apiV2 not configured.
 */
export function getApiClientV2(): TRPCClient<AppRouter> {
  if (!config) {
    throw new Error(
      'Web Shared API not configured. Please call configureWebShared() with API configuration before using apiV2.'
    );
  }
  if (!config.apiV2) {
    throw new Error(
      'TRPC client (apiV2) not configured. Please include apiV2 in your configureWebShared() call.'
    );
  }
  return config.apiV2;
}

/**
 * Safes router client. `AppRouter.safes` is optional because workers/main does
 * not implement it; the web client always talks to services/main which does.
 */
export function getSafesApiClient(): NonNullable<
  TRPCClient<AppRouter>['safes']
> {
  const safes = getApiClientV2().safes;
  if (!safes) {
    throw new Error(
      'Safes API is not available on this TRPC client (expected services/main).'
    );
  }
  return safes;
}

/**
 * Safes React Query proxy. Same optional-router caveat as
 * {@link getSafesApiClient}; use this for `queryOptions` / `mutationOptions`.
 */
export function getSafesTrpcClient(): NonNullable<
  TRPCOptionsProxy<AppRouter>['safes']
> {
  const safes = getTRPCClient().safes;
  if (!safes) {
    throw new Error(
      'Safes API is not available on this TRPC client (expected services/main).'
    );
  }
  return safes;
}

/**
 * Get the configured worker main URL.
 * Throws if configureWebShared has not been called or workerMainUrl not configured.
 */
export function getWorkerMainUrl(): string {
  if (!config) {
    throw new Error(
      'Web Shared API not configured. Please call configureWebShared() with API configuration before accessing workerMainUrl.'
    );
  }
  if (!config.workerMainUrl) {
    throw new Error(
      'Worker main URL not configured. Please include workerMainUrl in your configureWebShared() call.'
    );
  }
  return config.workerMainUrl;
}
